// V2 版式：funnel（前端 DOM 版）——对照后端 _shared/pptx/v2/layouts/funnel.ts 逐坐标移植
// 3–5 层居中横带逐层收窄；单 accent 明度渐变（shadeHex 顶层浅 → 底层浓）；
// 宽度值比例诚实（value/maxValue 直接驱动带宽，仅留 minBarRatio 下限）；
// 过窄层自动外置标签 + 引线；带内文字色随明度（浅带正文色/浓带 accentOn）。
// 几何取 V2GEO["funnel"]（deno dump 同源，勿手改数值）。

import { SPACING } from "../../lib/tokens.generated";
import { V2GEO } from "../../lib/v2geo.generated";
import { px, pt, titleSize, kickerOf, footOf } from "./shared";
import { shadeHex } from "./semantic";
import V2Chrome from "./Chrome";
import { EditableText } from "../SlideCanvas";
import type { V2LayoutProps } from "./V2Slide";

// deno dump 同源：与后端 funnel.ts GEO 一一对应
const GEO = V2GEO["funnel"] as any;

// funnel block 结构以后端 _shared/pptx/types.ts 为准（slides.ts Block 已同步）
type FunnelTier = { label: string; value: number };
type FunnelBlock = { type: "funnel"; unit?: string; tiers: FunnelTier[] };

export default function V2Funnel({ c, skin, page, total, editable, handlers }: V2LayoutProps) {
  const m = SPACING.marginX;
  const W = 13.333;

  const fi = c.blocks.findIndex((b) => (b as { type: string }).type === "funnel");
  const fb = fi >= 0 ? (c.blocks[fi] as unknown as FunnelBlock) : null;
  const tiers = fb ? fb.tiers.slice(0, 5) : [];
  const n = tiers.length;
  const maxV = Math.max(...tiers.map((t) => t.value), 1e-9);

  const plotH = GEO.chart.bottom - GEO.chart.top;
  const layerH = n > 0 ? plotH / n : plotH;
  const bandH = layerH * GEO.barHRatio;
  const centerX = W / 2;
  // 可画带宽上限：左右各留 nameW/valW + 引线余量（与后端同公式）
  const maxBarW = W - 2 * m - GEO.nameW - GEO.valW - GEO.leadGap * 2;

  return (
    <>
      <V2Chrome skin={skin} kicker={kickerOf(page, "funnel")} page={page} total={total} foot={footOf()} />

      {/* 页面标题（长度分档） */}
      <EditableText
        value={c.title} path="title" editable={editable} multiline
        onEdit={handlers.onEdit} onBeginEdit={handlers.onBeginEdit} onEndEdit={handlers.onEndEdit}
        style={{
          position: "absolute", left: px(GEO.title.x), top: px(GEO.title.y),
          width: px(GEO.title.w), height: px(GEO.title.h),
          fontSize: pt(titleSize(c.title, skin.scale.pageTitle)), color: skin.text,
          fontFamily: skin.fontHead, fontWeight: skin.serifHead ? 700 : 400, lineHeight: 1.15,
        }}
      />

      {tiers.map((t, i) => {
        const y = GEO.chart.top + i * layerH + (layerH - bandH) / 2;
        const ratio = t.value / maxV;
        const barW = Math.max(maxBarW * ratio, maxBarW * GEO.minBarRatio);
        const barX = centerX - barW / 2;
        // 色带：单 accent 明度渐变（顶层浅 → 底层浓，与后端同 amount 公式）
        const amount = 0.4 - 0.55 * (n > 1 ? i / (n - 1) : 0);
        const inline = barW >= GEO.minInlineW;
        const bandText = amount >= 0 ? skin.text : skin.accentOn;
        return (
          <div key={i}>
            {/* 层带 */}
            <div style={{
              position: "absolute", left: px(barX), top: px(y),
              width: px(barW), height: px(bandH),
              background: shadeHex(skin.accent, amount),
              borderRadius: px(Math.min(skin.radius, bandH / 2)),
            }} />
            {/* 层名：带内居中（放不下则外置左侧 + 引线） */}
            {inline ? (
              <EditableText
                value={t.label} path={`blocks.${fi}.tiers.${i}.label`} editable={editable}
                onEdit={handlers.onEdit} onBeginEdit={handlers.onBeginEdit} onEndEdit={handlers.onEndEdit}
                style={{
                  position: "absolute", left: px(barX), top: px(y),
                  width: px(barW), height: px(bandH),
                  fontSize: pt(skin.scale.body), color: bandText, fontFamily: skin.fontBody,
                  fontWeight: 700, textAlign: "center",
                  display: "flex", alignItems: "center", justifyContent: "center",
                  whiteSpace: "nowrap",
                }}
              />
            ) : (
              <>
                <div style={{
                  position: "absolute", left: px(barX - GEO.leadGap), top: px(y + bandH / 2),
                  width: px(GEO.leadGap), height: 1, background: skin.sub,
                }} />
                <EditableText
                  value={t.label} path={`blocks.${fi}.tiers.${i}.label`} editable={editable}
                  onEdit={handlers.onEdit} onBeginEdit={handlers.onBeginEdit} onEndEdit={handlers.onEndEdit}
                  style={{
                    position: "absolute", left: px(barX - GEO.leadGap - GEO.nameW), top: px(y),
                    width: px(GEO.nameW), height: px(bandH),
                    fontSize: pt(skin.scale.body), color: skin.text, fontFamily: skin.fontBody,
                    textAlign: "right", display: "flex", alignItems: "center", justifyContent: "flex-end",
                  }}
                />
              </>
            )}
            {/* 数值：带右端外侧 + 短引线（fontMono，比例诚实的值直接标注） */}
            <div style={{
              position: "absolute", left: px(barX + barW), top: px(y + bandH / 2),
              width: px(GEO.leadGap), height: 1, background: skin.sub,
            }} />
            <EditableText
              value={`${t.value.toLocaleString("en-US")}${fb?.unit ?? ""}`}
              path={`blocks.${fi}.tiers.${i}.value`} editable={editable}
              onEdit={handlers.onEdit} onBeginEdit={handlers.onBeginEdit} onEndEdit={handlers.onEndEdit}
              style={{
                position: "absolute", left: px(barX + barW + GEO.leadGap), top: px(y),
                width: px(GEO.valW), height: px(bandH),
                fontSize: pt(skin.scale.body), color: skin.text, fontFamily: skin.fontMono,
                display: "flex", alignItems: "center", whiteSpace: "nowrap",
              }}
            />
          </div>
        );
      })}
    </>
  );
}
